import { exportSurveyResultToFile } from "@/lib/export/crm/salesforce/exportSurveyToFile";
import { CrmSystem } from "../../_core/crmSystem";
import { BooleanForm, CheckboxForm, CurrencyForm, DateForm, NumberForm, SliderForm, TextForm } from "../_common/FieldForm";
import { BooleanRenderer, CheckboxRenderer, CurrencyRenderer, NumberRenderer, PickListMultipleRenderer, PickListRenderer, RadioRenderer, TextRenderer, TextareaRenderer } from "../_common/FieldRenderer";
import { booleanParser, dateParser, numberParser, semicolonListParser, stringParser } from "../_common/useGetExportData/parser/_core";
import { PickGroupRankForm } from "./FieldForm/PickGroupRankForm";
import { DateRenderer, SliderRenderer } from "./FieldRenderer";
import { PickGroupRankRenderer } from "./FieldRenderer/PickGroupRankRenderer";
import { RankRenderer } from "./FieldRenderer/RankRenderer";
import { UseFormReturn } from "react-hook-form";
import { QuestionFormSchemaType } from "@/features/question/components/organisms/QuestionForm";

class CollaborateCrmSystem extends CrmSystem<'collaborate'> {
    id = "collaborate" as const;
    name = "Collaborate";
        branding = {
        color: '#5A5A5A',
        logoUrl: undefined
    };
    exportHandler = exportSurveyResultToFile;
    fieldTypes = {
        'text': {
            formComponent: TextForm,
            rendererComponent: TextRenderer,
            name: 'Text',
            id: 'text',
            parser: stringParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'text',
                'airtable': 'singleLineText'
            },
            baseType: 'text'
        } as const,
        'textarea': {
            formComponent: TextForm,
            rendererComponent: TextareaRenderer,
            name: 'Textarea',
            id: 'textarea',
            parser: stringParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'textarea',
                'airtable': 'multilineText'
            },
            baseType: 'text'
        } as const,
        'number': {
            formComponent: NumberForm,
            rendererComponent: NumberRenderer,
            name: 'Number',
            id: 'number',
            parser: numberParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'number',
                'airtable': 'number'
            },
            baseType: 'number'
        } as const,
        'currency': {
            formComponent: CurrencyForm,
            rendererComponent: CurrencyRenderer,
            name: 'Currency',
            id: 'currency',
            parser: numberParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'currency',
                'airtable': 'currency'
            },
            baseType: 'number'
        } as const,
        'slider': {
            formComponent: SliderForm,
            rendererComponent: SliderRenderer,
            name: 'Slider',
            id: 'slider',
            parser: numberParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'slider',
                'airtable': 'number'
            },
            baseType: 'number'
        } as const,
        'checkbox': {
            formComponent: CheckboxForm,
            rendererComponent: CheckboxRenderer,
            name: 'Checkbox',
            id: 'checkbox',
            parser: semicolonListParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'checkbox',
                'airtable': 'multipleSelects'
            },
            baseType: 'list'
        } as const,
        'radio': {
            formComponent: CheckboxForm,
            rendererComponent: RadioRenderer,
            name: 'Radio',
            id: 'radio',
            parser: stringParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'radio',
                'airtable': 'singleSelect'
            },
            baseType: 'text'
        } as const,

        'picklist': {
            formComponent: CheckboxForm,
            rendererComponent: PickListRenderer,
            name: 'Picklist',
            id: 'picklist',
            parser: stringParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'radio',
                'airtable': 'singleSelect'
            },
            baseType: 'text'
        } as const,
        'picklist_multiple': {
            formComponent: CheckboxForm,
            rendererComponent: PickListMultipleRenderer,
            name: 'Picklist Multiple',
            id: 'picklist_multiple',
            parser: semicolonListParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'checkbox',
                'airtable': 'multipleSelects'
            },
            baseType: 'list'
        } as const,
        'boolean': {
            formComponent: BooleanForm,
            rendererComponent: BooleanRenderer,
            name: 'Boolean',
            id: 'boolean',
            parser: booleanParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'radio',
                'airtable': 'checkbox'
            },
            baseType: 'boolean'
        } as const,
        'rank': {
            formComponent: CheckboxForm,
            rendererComponent: RankRenderer,
            name: 'Rank',
            id: 'rank',
            parser: semicolonListParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'text',
                'airtable': 'rating'
            },
            baseType: 'list'
        } as const,
        'pick_group_rank': {
            formComponent: PickGroupRankForm,
            rendererComponent: PickGroupRankRenderer,
            name: 'Pick Group Rank',
            id: 'pick_group_rank',
            parser: stringParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'text',
                'airtable': 'multipleSelects'
            },
            baseType: 'list',
            resetForm: (form: UseFormReturn<QuestionFormSchemaType>) => {
                form.setValue("field_options", { groups: [], options: [] });
            }
        } as const,
        'date': {
            formComponent: DateForm,
            rendererComponent: DateRenderer,
            name: 'Date',
            id: 'date',
            parser: dateParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'date',
                'airtable': 'date'
            },
            baseType: 'number'
        } as const,
    };

    resetForm(
        field: keyof typeof this.fieldTypes,
        form: UseFormReturn<QuestionFormSchemaType>
    ) {
        switch (field) {
            case "pick_group_rank": {
                form.setValue("field_options", { groups: [], options: [] });
                break;
            }
        }
    }
}

export const collaborateCrmSystem = new CollaborateCrmSystem();